<script setup>
import { DEV_MODE } from '@/config/dev-mode';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { useTicketProduct } from '@/composables/saju-insight/useTicketProduct';
import { getAnalysisContentAPI, postAnalysisLogAPI } from '@/api/AnalysisApi';
import { addContentLogAPI } from '@/api/logServiceApi';
import { formatNumber } from '@/composables/functions';
import {
  MONTHLY_FORTUNE_TOC_CARDS,
  MONTHLY_FORTUNE_CARD_TYPE_ID,
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
  pickRandomCardSubs,
} from './tocCards';
import { getCoverIllustration } from './coverIllustrations';
import { getMonthlyCoverCopy } from './monthlyCoverCopy';
import { useMonthlyFortuneFreeData } from '@/composables/saju-insight/useMonthlyFortuneFreeData';
import { getMonthGanjiByYearMonth } from '@/utils/monthGanjiUtil';
import MonthlyFortuneDrawer from './MonthlyFortuneDrawer.vue';
import PopMonthlyFortuneNextMonth from './PopMonthlyFortuneNextMonth.vue';
import PopMonthlyFortunePurchase from './PopMonthlyFortunePurchase.vue';
import ConfirmComponent from '@/components/saju-insight/common/confirmComponent.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import AlertComponent from '@/components/common/Alert.vue';
import { skyKor, skyChn, earthKor, earthChn } from '@/composables/fiveMixin';

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const paymentsStore = usePaymentsStore();
const {
  fetchMonthlyFortuneChildContents,
  getMonthlyFortuneCardContent,
  getMonthlyFortuneCardPrice,
  monthlyFortuneChildContents,
} = useAnalysisServices();

const guestUserId = computed(() => route.params.id);

const emit = defineEmits(['payment-success']);

const props = defineProps({
  ticketCountData: { required: true },
  monthlyFortuneLogData: { required: true },
  hasPermission: { required: true },
  loadingTicket: { required: true },
});

const monthlyFortuneLogData = computed(() => {
  if (!props.monthlyFortuneLogData?.length) return null;
  return props.monthlyFortuneLogData[0];
});
// =============== 월 선택 (CoverPanel 과 공유) ===============
const selectedYear = computed({
  get: () => analysisStore.monthlyFortuneSelectedYear,
  set: (v) => (analysisStore.monthlyFortuneSelectedYear = v),
});
const selectedMonth = computed({
  get: () => analysisStore.monthlyFortuneSelectedMonth,
  set: (v) => (analysisStore.monthlyFortuneSelectedMonth = v),
});

/** 현재 달 기준 +0/+1/+2개월 */
const monthOptions = computed(() => {
  const list = [];
  const today = new Date();
  for (let i = 0; i < 3; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    list.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return list;
});

/** 구매 가능한 마지막 달(현재월+2)인지 — 이 달에는 '다음 달 미리보기' 노출 안 함 */
const isLastSelectableMonth = computed(() => {
  const last = monthOptions.value[monthOptions.value.length - 1];
  return selectedYear.value === last.year && selectedMonth.value === last.month;
});

// =============== 낭만월운 데이터 (구매여부에 따라 flag 자동 전환) ===============
/**
 * 해당 (year, month) 에서 실제 구매된 typeId 목록 (부모 10 + 자식 42~46 중 구매된 것).
 * base-service 는 analysisTypeId=10 고정으로 호출하고, 구매한 카테고리 번호를
 * suggestionFlag 콤마 문자열로 전달한다.
 */
const resolvePurchasedTypeIds = (year, month) => {
  if (!guestUserId.value) return [];
  const tids = [
    MONTHLY_FORTUNE_PARENT_TYPE_ID,
    ...Object.values(MONTHLY_FORTUNE_CARD_TYPE_ID),
  ];
  return tids.filter((tid) =>
    analysisStore.hasMonthlyFortuneAccess(guestUserId.value, year, month, tid),
  );
};

const purchasedMonthlyFortuneTypeIds = computed(() =>
  resolvePurchasedTypeIds(selectedYear.value, selectedMonth.value),
);

const { freeData, ensureFreeData, fetchFor, getFreeDataFor, invalidateMonth } =
  useMonthlyFortuneFreeData(guestUserId, selectedYear, selectedMonth, resolvePurchasedTypeIds);

/** 낭만월운 데이터 fetch 진행 중 플래그 — 응답 도착 전 로딩 유지 */
const isFetchingMonthlyFortune = ref(true);
const fetchCurrentMonthData = async () => {
  isFetchingMonthlyFortune.value = true;
  try {
    await ensureFreeData();
  } finally {
    isFetchingMonthlyFortune.value = false;
  }
};

/** 로딩 오버레이 노출 조건 — 권한/데이터 준비 전까지 노출 */
const isLoadingOverlayVisible = computed(
  () =>
    props.loadingTicket ||
    !isMonthLogsReady.value ||
    isFetchingMonthlyFortune.value ||
    !freeData.value,
);

/** 드로어/표지에 paid 섹션도 동일 데이터 객체로 전달 (구매 시 12개 섹션 포함) */
const paidData = freeData;

// =============== 경고 응답 (대운범위초과 등) ===============
/** 현재 선택월 freeData 의 경고 메시지 (없으면 빈 문자열) */
const warningMessage = computed(() => {
  const w = freeData.value?.['경고'];
  if (!w) return '';
  if (w['유형'] === '대운범위초과') {
    return w['메시지'] || '분석 기간이 대운 범위를 초과합니다.';
  }
  return w['메시지'] || '';
});

/** 경고 상태 — 모든 카드/결제 비활성화 트리거 */
const hasWarning = computed(() => !!warningMessage.value);

const warningAlertRef = ref(null);
/** (year, month) 별로 경고 알림을 한 번씩만 띄움 */
const shownWarningKeys = ref(new Set());
const openWarningAlert = () => {
  if (!warningMessage.value) return;
  nextTick(() => warningAlertRef.value?.openAlertDialog());
};
watch(
  warningMessage,
  (msg) => {
    if (!msg) return;
    const key = `${selectedYear.value}-${selectedMonth.value}`;
    if (shownWarningKeys.value.has(key)) return;
    shownWarningKeys.value.add(key);
    openWarningAlert();
  },
  { immediate: true },
);

/** "계사(癸巳), 2026년 05월" → "癸巳" 추출 */
const extractHanjaGanji = (str) => {
  if (!str) return '';
  const m = str.match(/\(([^)]+)\)/);
  return m ? m[1] : '';
};

/** year/month 의 月간지 (한자 2글자).
 * 응답 도착 전에도 즉시 표시되도록 로컬 月柱 산출 유틸을 우선 사용하고,
 * 응답이 있으면 응답 값으로 보정. */
const getMonthGanji = (year, month) => {
  const data = getFreeDataFor(year, month);
  const fromApi = data ? extractHanjaGanji(data['0_사주정보']?.현재월운) : '';
  return fromApi || getMonthGanjiByYearMonth(year, month);
};

const currentGanji = computed(() => getMonthGanji(selectedYear.value, selectedMonth.value));

/** 月干 / 月支 분리 (예: '癸巳' → { stem: '癸', branch: '巳' }) */
const ganjiParts = computed(() => {
  const g = currentGanji.value;
  if (!g || g.length < 2) return { stem: '', branch: '' };
  return { stem: g.slice(0, 1), branch: g.slice(-1) };
});

/** 양력 월 기반 일러스트 (간지 API 응답과 무관하게 즉시 결정) */
const coverIllustration = computed(() => getCoverIllustration(selectedMonth.value, 'pc'));

/** 카드별 무료데이터 description 오버라이드 (값 없으면 정적 기본값 유지) */
const freeCardDescriptionOverride = (cardKey) => {
  // 낭만편지: 무료데이터와 무관하게 월·이름으로 결정 (이름 없으면 '-'로 표기)
  if (cardKey === 'letter') {
    return `${selectedMonth.value}월의 ${analysisStore.userName || '-'}님에게`;
  }
  const data = freeData.value;
  if (!data) return null;
  switch (cardKey) {
    case 'energy': {
      const v = data['2_에너지활성도']?.활성도;
      return v != null ? `이달의 활성도: ${v}%` : null;
    }
    case 'romance':
      return data['4_연애감정']?.유형 ? `"${data['4_연애감정'].유형}"` : null;
    default:
      return null;
  }
};

/** 페이지 진입 시 1회 픽한 랜덤 서브 카피 (key → string) */
const randomCardSubs = ref(pickRandomCardSubs());

const isMonthLayerOpen = ref(false);
const monthSelectorRef = ref(null);
const toggleMonthLayer = () => {
  isMonthLayerOpen.value = !isMonthLayerOpen.value;
};

/** 드롭다운 바깥 클릭 시 닫기 */
onClickOutside(monthSelectorRef, () => {
  if (!isMonthLayerOpen.value) return;
  isMonthLayerOpen.value = false;
});
const handleSelectMonth = (opt) => {
  addContentLogAPI({
    contentType: 'am_monthly_select_month_opt3',
    option3: `${opt.year}-${String(opt.month).padStart(2, '0')}`,
  });
  selectedYear.value = opt.year;
  selectedMonth.value = opt.month;
  isMonthLayerOpen.value = false;
  fetchFor(opt.year, opt.month);
  ensureMonthLogs(opt.year, opt.month);
};

/** 해당 (year, month) 가 전체구매 됐는지 (typeId 10 또는 자식 42~46 모두) */
const isMonthFullyPurchased = (year, month) =>
  guestUserId.value
    ? analysisStore.isMonthlyFortuneMonthFullyPurchased(guestUserId.value, year, month)
    : false;

/** 해당 월의 부모/자식 권한(access+log)을 store 캐시에 채워둠 */
const ensureMonthLogs = (year, month) => {
  if (!guestUserId.value) return;
  if (analysisStore.hasMonthlyFortuneLogsCache(guestUserId.value, year, month)) return;
  analysisStore.fetchMonthlyFortuneLogs(guestUserId.value, year, month);
};

/** 우측 카드형 목차 스크롤 컨테이너 */
const tocRef = ref(null);

/** 외부(CoverPanel 등)에서 store 의 선택월이 바뀐 경우 동기화 */
watch([selectedYear, selectedMonth], ([y, m]) => {
  ensureMonthLogs(y, m);
  fetchCurrentMonthData();
  isMonthLayerOpen.value = false;
  // 월 변경 시 표지 목차 스크롤을 맨 위로 초기화
  nextTick(() => {
    if (tocRef.value) tocRef.value.scrollTop = 0;
  });
});

/** 구매한 카테고리가 늘어나면(결제 직후) 캐시 무효화 후 새 suggestionFlag 로 재요청 */
watch(
  purchasedMonthlyFortuneTypeIds,
  (newVal, oldVal) => {
    if ((newVal?.length || 0) > (oldVal?.length || 0)) {
      invalidateMonth(selectedYear.value, selectedMonth.value);
      fetchCurrentMonthData();
    }
  },
  { deep: true },
);

// ticket-detail 에서 ?openPurchase=1 로 진입한 경우 자동 노출
const showPurchasePopup = ref(false);
const clearOpenPurchaseQuery = () => {
  if (!route.query.openPurchase) return;
  const newQuery = { ...route.query };
  delete newQuery.openPurchase;
  router.replace({ path: route.path, query: newQuery });
};
const handlePurchasePopupSuccess = () => {
  // remount 후 자동 재오픈 방지를 위해 query 를 먼저 정리
  clearOpenPurchaseQuery();
  emit('payment-success', true);
};
const handlePurchasePopupClose = () => {
  showPurchasePopup.value = false;
  clearOpenPurchaseQuery();
};

onMounted(async () => {
  // 결제 직후 remount 됐는지 확인 (pending 카드가 있으면 사용자가 선택했던 월을 유지해야 함)
  const pending = analysisStore.consumeMonthlyFortunePendingDrawerCard();
  // 전체 패키지 구매처럼 drawer 자동 오픈은 없지만 선택월은 보존해야 하는 케이스
  const preserveMonth = analysisStore.consumeMonthlyFortunePreserveSelectedMonth();

  // ticket-detail → 낭만월운 이동 시 구매 팝업 자동 노출
  if (route.query.openPurchase === '1') {
    showPurchasePopup.value = true;
  }

  // 일반 진입 시에만 현재월로 초기화. 결제 후 remount 시에는 store 에 보존된 선택월 유지.
  if (!pending && !preserveMonth) {
    const today = new Date();
    selectedYear.value = today.getFullYear();
    selectedMonth.value = today.getMonth() + 1;
  }

  // 권한 캐시(월별 로그)를 먼저 채워야 fetchFor 가 올바른 flag/typeId 로 호출됨.
  // 구매된 사용자에게 flag=1 을 보내면 백엔드 에러가 나므로 순서가 중요하다.
  await Promise.all([
    guestUserId.value
      ? analysisStore.fetchMonthlyFortunePurchaseHistory(guestUserId.value)
      : Promise.resolve(),
    fetchMonthlyFortuneChildContents(),
    fetchBundleContent(),
    fetchBundleTicketProduct(),
  ]);

  // 현재 선택월은 로딩 플래그를 동반해 요청 (오버레이 노출/해제 트리거)
  fetchCurrentMonthData();
  monthOptions.value.forEach((opt) => {
    if (opt.year === selectedYear.value && opt.month === selectedMonth.value) {
      ensureMonthLogs(opt.year, opt.month);
      return;
    }
    fetchFor(opt.year, opt.month);
    ensureMonthLogs(opt.year, opt.month);
  });

  // 결제 직후 remount 됐다면 저장해둔 카드로 drawer 자동 재오픈
  if (pending) {
    drawerCard.value = pending;
    drawerOpen.value = true;
  }
});

// =============== 카드 구매 상태 ===============
/** 해당 카드가 현재 선택월 기준 구매(권한 보유) 됐는지 */
const isCardPurchased = (cardKey) => {
  const typeId = MONTHLY_FORTUNE_CARD_TYPE_ID[cardKey];
  if (!typeId) return false;
  return analysisStore.hasMonthlyFortuneAccess(
    guestUserId.value,
    selectedYear.value,
    selectedMonth.value,
    typeId,
  );
};

/** 현재 선택월의 권한 로그가 캐시에 적재됐는지 */
const isMonthLogsReady = computed(
  () =>
    !!guestUserId.value &&
    analysisStore.hasMonthlyFortuneLogsCache(
      guestUserId.value,
      selectedYear.value,
      selectedMonth.value,
    ),
);

// =============== 카드 처리 ===============
/** 다음달 라벨: "6월 갑오월(甲午月)" 형식 — 일단 selectedMonth+1 기준 */
const nextMonthGanji = computed(() => {
  // 우선: 현재월 freeData의 6_다음달미리보기.간지 (예: "갑오(甲午)")
  const fromCurrent = extractHanjaGanji(freeData.value?.['6_다음달미리보기']?.간지);
  if (fromCurrent) return fromCurrent;
  // 폴백: 다음 달 자체 freeData 캐시
  const next = new Date(selectedYear.value, selectedMonth.value, 1);
  return getMonthGanji(next.getFullYear(), next.getMonth() + 1);
});
const nextMonthDescription = computed(() => {
  const ganji = nextMonthGanji.value;
  if (!ganji) return '';
  const next = new Date(selectedYear.value, selectedMonth.value, 1);
  const nm = next.getMonth() + 1;
  const ganjiKor = hanjaGanjiToHangul(ganji);
  const base = `${nm}월 ${ganjiKor}월(${ganji}月)`;
  const headline = getMonthlyCoverCopy(nm)?.headline;
  const commaIdx = headline?.indexOf(',') ?? -1;
  const tail = commaIdx >= 0 ? headline.slice(commaIdx + 1).trim() : '';
  return tail ? `${base} - ${tail}` : base;
});

/** 카드 데이터: 정적 메타 + 동적 description / price 주입 */
const tocCards = computed(() =>
  MONTHLY_FORTUNE_TOC_CARDS.filter(
    (card) => !(card.key === 'nextMonth' && isLastSelectableMonth.value),
  ).map((card) => attachCardDynamicFields(card)),
);

const attachCardDynamicFields = (card) => {
  const dynamic = { ...card };
  if (card.key === 'nextMonth') {
    if (nextMonthDescription.value) dynamic.description = nextMonthDescription.value;
  } else {
    const override = freeCardDescriptionOverride(card.key);
    if (override) {
      dynamic.description = override;
    } else if (randomCardSubs.value[card.key]) {
      dynamic.description = randomCardSubs.value[card.key];
    }
  }
  if (card.isPaid) {
    dynamic.price = getMonthlyFortuneCardPrice(card.key);
    dynamic.purchased = isCardPurchased(card.key);
  }
  return dynamic;
};

// =============== 드로어 / 팝업 상태 ===============
const drawerOpen = ref(false);
const drawerCard = ref(null);

// Panel 구매내역에서 카드 클릭 시 drawer 열기
watch(
  () => analysisStore.monthlyFortuneOpenDrawerRequest,
  () => {
    const key = analysisStore.monthlyFortuneRequestedDrawerCardKey;
    if (!key) return;
    const card = MONTHLY_FORTUNE_TOC_CARDS.find((c) => c.key === key);
    if (!card) return;
    drawerCard.value = card;
    drawerOpen.value = true;
  },
);

const purchaseConfirmRef = ref(null);
const purchasePendingCard = ref(null);

const nextMonthPopupOpen = ref(false);

// 결제 완료 알림 팝업 — 확인을 눌러야 후속 동작(드로어 오픈/리마운트)이 실행되도록 한다
const purchaseSuccessAlertRef = ref(null);
const pendingPostPurchaseAction = ref(null);
const openPurchaseSuccessAlert = (afterConfirm) => {
  pendingPostPurchaseAction.value = afterConfirm;
  nextTick(() => purchaseSuccessAlertRef.value?.openAlertDialog());
};
const handlePurchaseSuccessConfirmed = () => {
  const action = pendingPostPurchaseAction.value;
  pendingPostPurchaseAction.value = null;
  if (typeof action === 'function') action();
};

// =============== 전체 패키지(typeId=10) 결제 ===============
/** 부모(typeId=10) 콘텐츠 — pointPrice / pointBasePrice */
const bundleContent = ref(null);
/** 결제 진행 중 락 (중복 클릭 방지) */
const isBundleProcessing = ref(false);
/** 'ticket' | 'point' — 확인 팝업 진입 시점에 결정 */
const bundlePaymentMethod = ref('point');

const fetchBundleContent = async () => {
  try {
    bundleContent.value = await getAnalysisContentAPI({
      analysisTypeId: MONTHLY_FORTUNE_PARENT_TYPE_ID,
    });
  } catch (e) {
    DEV_MODE && console.error('낭만월운 패키지 가격 조회 실패:', e);
  }
};

// =============== 분석권 업셀 (확인 팝업 내 안내 버튼) ===============
const { fetchTicketProductByTypeId } = useTicketProduct();
/** 월운(typeId=10) 할인 분석권 상품 정보 ({ boardId, articleId, perUnitPrice, discountPct }) */
const bundleTicketProduct = ref(null);

const fetchBundleTicketProduct = async () => {
  bundleTicketProduct.value = await fetchTicketProductByTypeId(MONTHLY_FORTUNE_PARENT_TYPE_ID);
};

/** 확인 팝업의 업셀 버튼 클릭 → 월운 분석권 상품 상세로 이동 (없으면 분석권 목록) */
const goBundleTicketProduct = () => {
  addContentLogAPI({ contentType: 'am_monthly_bundle_payment_opt3', option3: '분석권상품' });
  if (bundleTicketProduct.value?.articleId) {
    router.push(
      `/purchase/ticket-detail/${bundleTicketProduct.value.boardId}/${bundleTicketProduct.value.articleId}`,
    );
  } else {
    router.push('/purchase/tickets');
  }
};

/** 분석권 보유 수량 (parent typeId=10 기준) */
const bundleTicketCount = computed(() => props.ticketCountData?.totalCount || 0);

/** 해당 월 패키지 구매 완료 여부 */
const isBundlePurchased = computed(() =>
  isMonthFullyPurchased(selectedYear.value, selectedMonth.value),
);

/** 현재 선택월에 이미 구매한 자식 카드가 1개 이상 존재하는지 */
const hasAnyChildPurchasedInMonth = computed(() => {
  if (!guestUserId.value) return false;
  return Object.values(MONTHLY_FORTUNE_CARD_TYPE_ID).some((typeId) =>
    analysisStore.hasMonthlyFortuneAccess(
      guestUserId.value,
      selectedYear.value,
      selectedMonth.value,
      typeId,
    ),
  );
});

/** 구매 전 패키지 배너 노출 조건 */
const showBundleBanner = computed(() => {
  if (!bundleContent.value) return false;
  if (!isMonthLogsReady.value) return false;
  return !isBundlePurchased.value;
});

/** 구매 완료 배너 노출 조건 */
const showBundlePurchasedBanner = computed(() => {
  if (!isMonthLogsReady.value) return false;
  return isBundlePurchased.value;
});

const bundlePointPrice = computed(() => bundleContent.value?.pointPrice ?? null);
/** 개별 카드 5개(typeId 42~46) pointPrice 합계 — 자식 콘텐츠 캐시 기반 */
const bundleBasePrice = computed(() => {
  const map = monthlyFortuneChildContents.value || {};
  const tids = Object.values(MONTHLY_FORTUNE_CARD_TYPE_ID);
  const sum = tids.reduce((acc, tid) => acc + (map[tid]?.pointPrice ?? 0), 0);
  return sum > 0 ? sum : null;
});
const bundleDiscountPercent = computed(() => {
  const base = bundleBasePrice.value;
  const price = bundlePointPrice.value;
  if (!base || !price || base <= price) return 0;
  return Math.round(((base - price) / base) * 100);
});


const bundleConfirmRef = ref(null);
/** 결제 확인 팝업 텍스트 */
const bundleConfirmTitle = computed(() => {
  return bundlePaymentMethod.value === 'ticket'
    ? '낭만월운 분석권을 사용하시겠습니까?'
    : `${selectedMonth.value}월 월운세 전체 패키지`;
});
const bundleConfirmTitle2 = computed(() => {
  return bundlePaymentMethod.value === 'ticket' ? '' : '구매 후 열람하시겠습니까?';
});
const bundleConfirmWarning = computed(() => {
  if (bundlePaymentMethod.value === 'ticket') return '낭만월운 분석권 1건 사용';
  return `결제 포인트 ${formatNumber(bundlePointPrice.value ?? 0)} P`;
});
const bundleConfirmText = computed(() =>
  bundlePaymentMethod.value === 'ticket' ? '사용' : '구매',
);

const bundlePreConfirmRef = ref(null);

const proceedBundlePurchase = () => {
  if (isBundleProcessing.value) return;
  const price = bundlePointPrice.value;
  if (price == null) return;

  // 1) 분석권 보유 시 우선 사용
  if (bundleTicketCount.value > 0) {
    bundlePaymentMethod.value = 'ticket';
    nextTick(() => bundleConfirmRef.value?.openConfirmDialog());
    return;
  }

  // 2) 포인트 잔액 확인
  if ((paymentsStore?.points ?? 0) < price) {
    popAnalysisInfoType.value = bundleContent.value;
    showPopAnalysisInfo.value = true;
    return;
  }

  // 3) 포인트 결제 확인
  bundlePaymentMethod.value = 'point';
  nextTick(() => bundleConfirmRef.value?.openConfirmDialog());
};

/** 패키지 구매하기 클릭 — 분석권 우선, 없으면 포인트, 둘 다 없으면 충전 팝업 */
const handleBundlePurchaseClick = () => {
  addContentLogAPI({ contentType: 'am_monthly_btn_bundle_purchase' });
  if (hasWarning.value) {
    openWarningAlert();
    return;
  }
  if (isBundleProcessing.value) return;

  // 이미 구매한 자식 컨텐츠가 있으면 확인 팝업 노출 후 진행
  if (hasAnyChildPurchasedInMonth.value) {
    nextTick(() => bundlePreConfirmRef.value?.openConfirmDialog());
    return;
  }

  proceedBundlePurchase();
};

/** 패키지 결제 확정 — analysisTypeId=10 으로 결제 */
const handleBundlePurchaseConfirm = async () => {
  addContentLogAPI({
    contentType: 'am_monthly_bundle_payment_opt3',
    option3: bundlePaymentMethod.value === 'ticket' ? '이용권' : '포인트',
  });
  if (isBundleProcessing.value) return;
  isBundleProcessing.value = true;
  try {
    const res = await postAnalysisLogAPI({
      guestUserId: guestUserId.value,
      analysisTypeId: MONTHLY_FORTUNE_PARENT_TYPE_ID,
      paymentMethod: bundlePaymentMethod.value,
      year: selectedYear.value,
      month: selectedMonth.value,
    });
    if (res?.guestUserId || res?.analysisLogId) {
      await Promise.all([
        analysisStore.fetchMonthlyFortuneLogs(
          guestUserId.value,
          selectedYear.value,
          selectedMonth.value,
          { force: true }, // 구매 직후 해당 월 권한 강제 갱신
        ),
        paymentsStore.getOwnPoint(),
        paymentsStore.fetchTicketCount?.(),
      ]);
      openPurchaseSuccessAlert(() => {
        // remount 후에도 구매한 월이 유지되도록 플래그 저장
        analysisStore.setMonthlyFortunePreserveSelectedMonth();
        emit('payment-success', true);
      });
    }
  } catch (e) {
    DEV_MODE && console.error('낭만월운 패키지 결제 실패:', e);
  } finally {
    isBundleProcessing.value = false;
  }
};

/** 포인트 부족 팝업 */
const showPopAnalysisInfo = ref(false);
const popAnalysisInfoType = ref(null);

/** 로그 전송용 카드 키 → 한글 매핑 (option3 값) */
const MONTHLY_CARD_LOG_LABEL = {
  letter: '편지',
  energy: '에너지',
  lucky: '행운',
  health: '건강',
  romance: '연애감정', // 무료 카드(내 연애 감정) — 유료 romanceStyle('연애')과 집계 분리
  nextMonth: '다음달',
  romanceStyle: '연애',
  money: '재물',
  capability: '업무',
  caution: '조심',
  mode: '모드',
};

/** 카드 클릭 핸들러 */
const handleCardClick = (card) => {
  // 무료/유료 = 해당 카드 분석내역(구매) 보유 여부로 판정. 보유=유료, 미보유=무료.
  addContentLogAPI({
    contentType: isCardPurchased(card?.key)
      ? 'am_monthly_card_click_paid_opt3'
      : 'am_monthly_card_click_free_opt3',
    option3: MONTHLY_CARD_LOG_LABEL[card?.key] || card?.key || '',
  });
  if (hasWarning.value) {
    openWarningAlert();
    return;
  }
  if (card.key === 'nextMonth') {
    nextMonthPopupOpen.value = true;
    return;
  }
  if (!card.isPaid || isCardPurchased(card.key)) {
    drawerCard.value = card;
    drawerOpen.value = true;
    return;
  }
  // 유료 카드 + 미구매 → 결제 흐름
  const cardContent = getMonthlyFortuneCardContent(card.key);
  if (!cardContent || cardContent.pointPrice == null) {
    DEV_MODE && console.warn('낭만월운 카드 가격 정보 없음:', card.key);
    return;
  }

  if ((paymentsStore?.points ?? 0) < cardContent.pointPrice) {
    // 포인트 부족 → 충전 팝업
    popAnalysisInfoType.value = cardContent;
    showPopAnalysisInfo.value = true;
    return;
  }

  // 포인트 충분 → 결제 확인 팝업
  purchasePendingCard.value = { ...card, content: cardContent };
  nextTick(() => {
    purchaseConfirmRef.value?.openConfirmDialog();
  });
};

/** 결제 확정 — 실제 결제 API 호출 */
const handlePurchaseConfirm = async () => {
  const pending = purchasePendingCard.value;
  if (!pending?.key) return;
  addContentLogAPI({ contentType: 'am_monthly_card_payment_opt3', option3: pending.key });
  const typeId = MONTHLY_FORTUNE_CARD_TYPE_ID[pending.key];
  if (!typeId) return;

  try {
    const res = await postAnalysisLogAPI({
      guestUserId: guestUserId.value,
      analysisTypeId: typeId,
      paymentMethod: 'point',
      year: selectedYear.value,
      month: selectedMonth.value,
    });
    if (res?.guestUserId || res?.analysisLogId) {
      // 권한 캐시 갱신 + 포인트 갱신
      await Promise.all([
        analysisStore.fetchMonthlyFortuneLogs(
          guestUserId.value,
          selectedYear.value,
          selectedMonth.value,
          { force: true }, // 구매 직후 해당 월 권한 강제 갱신
        ),
        paymentsStore.getOwnPoint(),
      ]);
      // parent(analysis/index.vue)가 결제 후 componentKey 를 증가시켜 cover 를 remount 하므로,
      // 여기서 drawer 를 직접 열어도 곧바로 사라진다. 대신 store 에 저장해두고
      // remount 직후 onMounted 에서 다시 연다.
      // 단, "구매 완료" 알림을 먼저 띄우고 [확인] 후에 pending 저장 + emit 을 실행한다.
      openPurchaseSuccessAlert(() => {
        analysisStore.setMonthlyFortunePendingDrawerCard(pending);
        emit('payment-success', true);
      });
    }
  } catch (e) {
    DEV_MODE && console.error('낭만월운 카드 결제 실패:', e);
  } finally {
    purchasePendingCard.value = null;
  }
};

/** 포인트 부족 팝업 충전 */
const handleChargePopAnalysisInfo = (shortageAmount) => {
  addContentLogAPI({ contentType: 'am_monthly_btn_charge_point' });
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount },
  });
  showPopAnalysisInfo.value = false;
};

const handleClosePopAnalysisInfo = () => {
  showPopAnalysisInfo.value = false;
};

/** 한자 간지 → 한글 변환 (예: '甲午' → '갑오') */
const hanjaGanjiToHangul = (ganji) => {
  if (!ganji) return '';
  return ganji
    .split('')
    .map((c) => {
      const skyIdx = skyChn.indexOf(c);
      if (skyIdx >= 0) return skyKor[skyIdx];
      const earthIdx = earthChn.indexOf(c);
      if (earthIdx >= 0) return earthKor[earthIdx];
      return c;
    })
    .join('');
};

/** 다음 달 라벨 (팝업 헤더용) */
const nextMonthLabel = computed(() => {
  const next = new Date(selectedYear.value, selectedMonth.value, 1);
  const nm = next.getMonth() + 1;
  const ganji = nextMonthGanji.value;
  if (!ganji) return `${nm}월`;
  const ganjiKor = hanjaGanjiToHangul(ganji);
  return `${nm}월 ${ganjiKor}월(${ganji}月)`;
});

/** 다음 달 미리보기 → 다음 달로 이동 */
const handleGoNextMonth = () => {
  const next = new Date(selectedYear.value, selectedMonth.value, 1);
  selectedYear.value = next.getFullYear();
  selectedMonth.value = next.getMonth() + 1;
  fetchFor(selectedYear.value, selectedMonth.value);
  ensureMonthLogs(selectedYear.value, selectedMonth.value);
};
</script>

<template>
  <div class="mf-cover">
    <AnalysisLoadingOverlay
      :loading="isLoadingOverlayVisible"
      message="낭만월운 분석중입니다!"
    />
    <!-- 좌측 표지 -->
    <div class="mf-cover__left">
      <div class="mf-title-area">
        <p class="mf-title-name">
          <span class="mf-title-name__user">{{ analysisStore.userName || '-' }}</span>
          <span class="mf-title-name__suffix">님의 낭만월운</span>
        </p>
        <div class="mf-title-row">
          <p class="mf-title-month">
            {{ selectedMonth }}월<template v-if="ganjiParts.stem || ganjiParts.branch"
              >(<span>{{ ganjiParts.stem }}</span
              ><span class="mf-highlight-branch">{{ ganjiParts.branch }}</span
              >月)</template
            >운세
          </p>
          <div ref="monthSelectorRef" class="mf-month-selector">
            <button class="mf-month-trigger" type="button" @click="toggleMonthLayer">
              <span class="mf-month-trigger__label">
                {{ selectedMonth }}월<template v-if="ganjiParts.stem || ganjiParts.branch"
                  >({{ ganjiParts.stem }}{{ ganjiParts.branch }})</template
                >
              </span>
              <svg
                class="mf-month-trigger__arrow"
                :class="{ open: isMonthLayerOpen }"
                width="24"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12.3341 13.7C12.1441 13.8706 11.8559 13.8706 11.6659 13.7L5.17671 7.872C4.83558 7.56564 5.0523 7 5.5108 7L18.4892 7C18.9477 7 19.1644 7.56564 18.8233 7.872L12.3341 13.7Z"
                  fill="#333"
                />
              </svg>
            </button>
            <div v-if="isMonthLayerOpen" class="mf-month-layer">
              <div class="mf-month-layer__group">
                <button
                  v-for="opt in monthOptions"
                  :key="`${opt.year}-${opt.month}`"
                  type="button"
                  class="mf-month-layer__item"
                  :class="{
                    active: opt.month === selectedMonth && opt.year === selectedYear,
                  }"
                  @click="handleSelectMonth(opt)"
                >
                  <span class="mf-month-layer__item-label">{{ opt.month }}월</span>
                  <span
                    v-if="isMonthFullyPurchased(opt.year, opt.month)"
                    class="mf-month-layer__badge"
                    >구매완료</span
                  >
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 봉투 일러스트: 月支별 통합 SVG -->
      <div class="mf-envelope">
        <img class="mf-envelope__img" :src="coverIllustration" alt="monthly fortune illustration" />
      </div>
    </div>

    <!-- 우측 카드형 목차 -->
    <div ref="tocRef" class="mf-toc">
      <!-- 전체 패키지 구매 완료 -->
      <div v-if="showBundlePurchasedBanner" class="mf-bundle-done">
        <svg
          class="mf-bundle-done__check"
          viewBox="0 0 72 72"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M34.7438 9.52545C35.9365 8.19039 38.0635 8.19039 39.2562 9.52545C40.2057 10.5883 41.8038 10.8414 43.0353 10.124C44.5822 9.22286 46.6051 9.88012 47.3269 11.5184C47.9015 12.8227 49.3431 13.5572 50.736 13.2555C52.4857 12.8765 54.2065 14.1267 54.3867 15.9078C54.5301 17.3258 55.6742 18.4699 57.0922 18.6133C58.8733 18.7935 60.1235 20.5143 59.7445 22.264C59.4428 23.6569 60.1773 25.0985 61.4816 25.6731C63.1199 26.3949 63.7771 28.4178 62.876 29.9647C62.1586 31.1962 62.4117 32.7943 63.4746 33.7438C64.8096 34.9365 64.8096 37.0635 63.4746 38.2562C62.4117 39.2057 62.1586 40.8038 62.876 42.0353C63.7771 43.5822 63.1199 45.6051 61.4816 46.3269C60.1773 46.9015 59.4428 48.3431 59.7445 49.736C60.1235 51.4857 58.8733 53.2065 57.0922 53.3867C55.6742 53.5301 54.5301 54.6742 54.3867 56.0922C54.2065 57.8733 52.4857 59.1235 50.736 58.7445C49.3431 58.4428 47.9015 59.1773 47.3269 60.4816C46.6051 62.1199 44.5822 62.7771 43.0353 61.876C41.8038 61.1586 40.2057 61.4117 39.2562 62.4746C38.0635 63.8096 35.9365 63.8096 34.7438 62.4746C33.7943 61.4117 32.1962 61.1586 30.9647 61.876C29.4178 62.7771 27.3949 62.1199 26.6731 60.4816C26.0985 59.1773 24.6569 58.4428 23.264 58.7445C21.5143 59.1235 19.7935 57.8733 19.6133 56.0922C19.4699 54.6742 18.3258 53.5301 16.9078 53.3867C15.1267 53.2065 13.8765 51.4857 14.2555 49.736C14.5572 48.3431 13.8227 46.9015 12.5184 46.3269C10.8801 45.6051 10.2229 43.5822 11.124 42.0353C11.8414 40.8038 11.5883 39.2057 10.5254 38.2562C9.19039 37.0635 9.19039 34.9365 10.5254 33.7438C11.5883 32.7943 11.8414 31.1962 11.124 29.9647C10.2229 28.4178 10.8801 26.3949 12.5184 25.6731C13.8227 25.0985 14.5572 23.6569 14.2555 22.264C13.8765 20.5143 15.1267 18.7935 16.9078 18.6133C18.3258 18.4699 19.4699 17.3258 19.6133 15.9078C19.7935 14.1267 21.5143 12.8765 23.264 13.2555C24.6569 13.5572 26.0985 12.8227 26.6731 11.5184C27.3949 9.88012 29.4178 9.22286 30.9647 10.124C32.1962 10.8414 33.7943 10.5883 34.7438 9.52545Z"
            fill="#5E9F1C"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M50 30.1005L33.181 47L24 37.7751L27.0858 34.6746L33.181 40.7989L46.9142 27L50 30.1005Z"
            fill="#fff"
          />
        </svg>
        <div class="mf-bundle-done__center">
          <p class="mf-bundle-done__title">
            <span>{{ selectedMonth }}월 월운세 전체 패키지 </span>
            <span class="mf-bundle-done__title-accent">구매 완료</span>
              <svg
                class="mf-bundle-done__pop-svg"
                viewBox="0 0 50 32"
                fill="none"
                aria-hidden="true"
              >
                <!-- P 뱃지 (rotate 4.6deg) -->
                <g transform="rotate(4.6 16 16)">
                  <circle
                    cx="16" cy="16" r="13"
                    fill="#9BC86D" stroke="#5E9F1C" stroke-width="1.5"
                  />
                  <text
                    x="16" y="17"
                    text-anchor="middle" dominant-baseline="middle"
                    font-family="Pretendard" font-weight="800" font-size="16" fill="#FFF7F0"
                  >P</text>
                </g>
                <!-- Figma Rectangle 4569 (P 뱃지 우상단 슬래시) - 회전 -136.91° -->
                <g transform="rotate(-136.91 32 -1)">
                  <path
                    transform="translate(29.17 -7.79)"
                    d="M1.23056 0.89489C1.28433 0.386129 1.71342 0 2.22502 0H3.43197C3.94357 0 4.37266 0.386129 4.42643 0.894891L5.65134 12.484C5.71377 13.0746 5.25075 13.5891 4.65688 13.5891H1.00011C0.406237 13.5891 -0.0567741 13.0746 0.00564734 12.484L1.23056 0.89489Z"
                    fill="#9BC86D"
                  />
                </g>
                <!-- Figma Rectangle 4570 (그 우측 슬래시) - 회전 -111.34° -->
                <g transform="rotate(-111.34 38 6)">
                  <path
                    transform="translate(35.16 -1.60)"
                    d="M1.25184 0.90593C1.30037 0.392374 1.73157 0 2.24741 0H3.43419C3.95003 0 4.38123 0.392375 4.42975 0.905931L5.67707 14.1068C5.73252 14.6936 5.27095 15.2008 4.68151 15.2008H1.00009C0.410643 15.2008 -0.0509267 14.6936 0.00452191 14.1068L1.25184 0.90593Z"
                    fill="#9BC86D"
                  />
                </g>
              </svg>
          </p>
          <p class="mf-bundle-done__desc">이번 달 운세 항목 전체를 자유롭게 확인할 수 있어요!</p>
        </div>
        <img
          class="mf-bundle-done__gift"
          src="@manseImages/monthly-fortune/bundle-gift-purchased.png"
          alt=""
        />
      </div>

      <!-- 전체 패키지 (typeId=10) -->
      <div v-if="showBundleBanner" class="mf-bundle">
        <div class="mf-bundle__main">
        <div class="mf-bundle__badge">
          <svg
            class="mf-bundle__badge-star"
            viewBox="0 0 70 70"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M32.76 1.0C33.95 -0.33 36.04 -0.33 37.23 1.0L38.28 2.17C39.23 3.23 40.79 3.48 42.02 2.76L43.37 1.97C44.92 1.07 46.91 1.72 47.63 3.36L48.26 4.79C48.84 6.09 50.25 6.81 51.64 6.51L53.17 6.18C54.92 5.8 56.61 7.03 56.79 8.81L56.95 10.36C57.09 11.78 58.21 12.9 59.63 13.05L61.19 13.2C62.97 13.39 64.19 15.08 63.82 16.82L63.48 18.35C63.18 19.75 63.9 21.16 65.21 21.73L66.64 22.36C68.28 23.09 68.92 25.07 68.02 26.62L67.23 27.97C66.52 29.2 66.76 30.77 67.83 31.72L68.99 32.76C70.33 33.95 70.33 36.04 68.99 37.23L67.83 38.28C66.76 39.23 66.52 40.79 67.23 42.02L68.02 43.37C68.92 44.92 68.28 46.91 66.64 47.63L65.21 48.26C63.9 48.84 63.18 50.25 63.48 51.64L63.82 53.17C64.19 54.92 62.97 56.61 61.19 56.79L59.63 56.95C58.21 57.09 57.09 58.21 56.95 59.63L56.79 61.19C56.61 62.97 54.92 64.19 53.17 63.82L51.64 63.48C50.25 63.18 48.84 63.9 48.26 65.21L47.63 66.64C46.91 68.28 44.92 68.92 43.37 68.02L42.02 67.23C40.79 66.52 39.23 66.76 38.28 67.83L37.23 68.99C36.04 70.33 33.95 70.33 32.76 68.99L31.72 67.83C30.77 66.76 29.2 66.52 27.97 67.23L26.62 68.02C25.07 68.92 23.09 68.28 22.36 66.64L21.73 65.21C21.16 63.9 19.75 63.18 18.35 63.48L16.82 63.82C15.08 64.19 13.39 62.97 13.2 61.19L13.05 59.63C12.9 58.21 11.78 57.09 10.36 56.95L8.81 56.79C7.03 56.61 5.8 54.92 6.18 53.17L6.51 51.64C6.81 50.25 6.09 48.84 4.79 48.26L3.36 47.63C1.72 46.91 1.07 44.92 1.97 43.37L2.76 42.02C3.48 40.79 3.23 39.23 2.17 38.28L1.0 37.23C-0.33 36.04 -0.33 33.95 1.0 32.76L2.17 31.72C3.23 30.77 3.48 29.2 2.76 27.97L1.97 26.62C1.07 25.07 1.72 23.09 3.36 22.36L4.79 21.73C6.09 21.16 6.81 19.75 6.51 18.35L6.18 16.82C5.8 15.08 7.03 13.39 8.81 13.2L10.36 13.05C11.78 12.9 12.9 11.78 13.05 10.36L13.2 8.81C13.39 7.03 15.08 5.8 16.82 6.18L18.35 6.51C19.75 6.81 21.16 6.09 21.73 4.79L22.36 3.36C23.09 1.72 25.07 1.07 26.62 1.97L27.97 2.76C29.2 3.48 30.77 3.23 31.72 2.17L32.76 1.0Z"
              fill="#FF8800"
            />
          </svg>
          <div class="mf-bundle__badge-text">
            <span>{{ bundleDiscountPercent }}%</span>
            <span>할인</span>
          </div>
        </div>
        <div class="mf-bundle__center">
          <p class="mf-bundle__title">{{ selectedMonth }}월 월운세 전체 패키지</p>
          <div class="mf-bundle__price-box">
            <span class="mf-bundle__price-label mf-bundle__price-label--left"
              >개별 구매 합계</span
            >
            <svg
              class="mf-bundle__arrow"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M11.25 15H6.75V9H3.12L9 3.12L14.88 9H11.25V15Z"
                fill="#575757"
                transform="rotate(90 9 9)"
              />
            </svg>
            <span class="mf-bundle__price-label mf-bundle__price-label--right">패키지</span>
            <span class="mf-bundle__price-base"
              >{{ formatNumber(bundleBasePrice ?? 0) }} P</span
            >
            <span class="mf-bundle__price-final"
              >{{ formatNumber(bundlePointPrice ?? 0) }} P</span
            >
          </div>
        </div>
        </div>
        <div class="mf-bundle__right">
          <img
            class="mf-bundle__gift"
            src="@manseImages/monthly-fortune/bundle-gift.png"
            alt=""
          />
        <button
          type="button"
          class="mf-bundle__btn"
          :disabled="isBundleProcessing || hasWarning"
          @click="handleBundlePurchaseClick"
        >
          <span class="mf-bundle__btn-label">구매하기</span>
          <span class="mf-bundle__btn-price"
            >{{ formatNumber(bundlePointPrice ?? 0) }}P</span
          >
        </button>
        </div>
      </div>

      <button
        v-for="item in tocCards"
        :key="item.key"
        type="button"
        class="mf-toc__card"
        :class="[
          `mf-toc__card--${item.theme}`,
          { 'mf-toc__card--disabled': hasWarning },
        ]"
        :disabled="hasWarning"
        @click="handleCardClick(item)"
      >
        <div class="mf-toc__card-main">
          <div class="mf-toc__card-title">{{ item.emoji }} {{ item.title }}</div>
          <div class="mf-toc__card-desc">{{ item.description }}</div>
        </div>
        <div class="mf-toc__card-right">
          <span
            v-if="item.isPaid && !item.purchased"
            class="mf-toc__card-price"
          >
            {{ item.price != null ? `${formatNumber(item.price)} P` : '-' }}
          </span>
          <span v-else class="mf-toc__card-badge">
            {{ item.isPaid ? '구매완료' : '무료' }}
          </span>
        </div>
      </button>
    </div>

    <!-- 카드 컨텐츠 드로어 -->
    <MonthlyFortuneDrawer
      v-model="drawerOpen"
      :card="drawerCard"
      :freeData="freeData"
      :paidData="paidData"
      :selectedMonth="selectedMonth"
      :selectedYear="selectedYear"
      :userName="analysisStore.userName || ''"
    />

    <!-- 패키지 결제 확인 팝업 -->
    <div class="mf-purchase-dialog">
      <ConfirmComponent
        ref="bundleConfirmRef"
        :title="bundleConfirmTitle"
        :title2="bundleConfirmTitle2"
        :warning="bundleConfirmWarning"
        :text="bundleConfirmText"
        :doUrl="handleBundlePurchaseConfirm"
      >
        <template v-if="bundlePaymentMethod === 'point' && bundleTicketProduct" #extra>
          <button type="button" class="mf-bundle-upsell" @click="goBundleTicketProduct">
            분석권으로 구매하면 더 저렴해요
            <span v-if="bundleTicketProduct.discountPct > 0" class="mf-bundle-upsell__pct"
              >최대 {{ bundleTicketProduct.discountPct }}% 할인</span
            >
            <span class="mf-bundle-upsell__arrow" aria-hidden="true">›</span>
          </button>
        </template>
      </ConfirmComponent>
    </div>

    <!-- 패키지 구매 전 기존 구매 컨텐츠 안내 팝업 -->
    <div class="mf-purchase-dialog">
      <ConfirmComponent
        ref="bundlePreConfirmRef"
        title="잠깐, 확인해주세요"
        title2="이미 구매하신 컨텐츠가 있어요."
        warning="월 전체 패키지를 구매하시겠어요?"
        text="구매하기"
        :doUrl="proceedBundlePurchase"
      />
    </div>

    <!-- 결제 확인 팝업 (기존 confirmComponent 재사용) -->
    <div class="mf-purchase-dialog">
      <ConfirmComponent
        ref="purchaseConfirmRef"
        :title="purchasePendingCard?.title || ''"
        title2="구매 후 열람하시겠습니까?"
        :warning="`결제 포인트 ${formatNumber(purchasePendingCard?.content?.pointPrice ?? 0)} P`"
        text="구매"
        :doUrl="handlePurchaseConfirm"
      />
    </div>

    <!-- 포인트 부족 팝업 -->
    <PopAnalysisInfo
      :isVisible="showPopAnalysisInfo"
      :analysisType="popAnalysisInfoType"
      @close="handleClosePopAnalysisInfo"
      @charge="handleChargePopAnalysisInfo"
    />

    <!-- 결제 완료 알림 팝업 — 확인 시 드로어/리마운트 진행 -->
    <AlertComponent
      ref="purchaseSuccessAlertRef"
      title="구매가 완료되었습니다."
      :nextUrl="handlePurchaseSuccessConfirmed"
    />

    <!-- 경고 팝업 (대운범위초과 등) -->
    <AlertComponent ref="warningAlertRef" :title="warningMessage" />

    <!-- 다음 달 미리보기 랜덤 팝업 -->
    <PopMonthlyFortuneNextMonth
      v-model="nextMonthPopupOpen"
      :nextMonthLabel="nextMonthLabel"
      :nextMonthData="freeData?.['6_다음달미리보기'] || null"
      @goNextMonth="handleGoNextMonth"
    />

    <!-- ticket-detail 에서 진입 시 자동 노출되는 월 선택 구매 팝업 -->
    <PopMonthlyFortunePurchase
      :visible="showPurchasePopup"
      :guestUserId="guestUserId"
      @close="handlePurchasePopupClose"
      @payment-success="handlePurchasePopupSuccess"
    />
  </div>
</template>

<style scoped lang="scss">
@import 'src/assets/nangman_style/_analysis_main_color.scss';

.mf-cover {
  display: flex;
  width: 100%;
  height: 822px;
  align-items: stretch;
  background: #fff;
  overflow: hidden;
}

// ─── 좌측 표지 ─────────────────────────────────────────────
.mf-cover__left {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  background: rgba(94, 159, 28, 0.05);
  position: relative;
  padding: 80px 40px 0;
  box-sizing: border-box;
  overflow: hidden;
}

.mf-title-area {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
}

.mf-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.mf-title-name {
  display: flex;
  align-items: baseline;
  width: 100%;
  color: #333;
  font-family: Pretendard;
  font-size: 32px;
  font-weight: 400;
  line-height: 44px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
}

.mf-title-name__user {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.mf-title-name__suffix {
  flex-shrink: 0;
}

.mf-title-month {
  color: #333;
  font-family: Pretendard;
  font-size: 32px;
  font-weight: 700;
  line-height: 44px;
  margin: 0;
}

.mf-highlight-branch {
  color: #c23535;
}

// 월 선택 드롭다운 (타이틀 두 번째 줄 우측)
.mf-month-selector {
  position: relative;
  flex-shrink: 0;
}
.mf-month-trigger {
  display: inline-flex;
  align-items: center;
  padding: 12px 16px 8px;
  border: none;
  border-bottom: 1px solid #333;
  background: transparent;
  width: 129px;
  height: 40px;
  border-radius: 5px 5px 0 0;
  justify-content: space-between;
  cursor: pointer;
  box-sizing: border-box;

  &__label {
    color: #333;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
  }

  &__arrow {
    transition: transform 0.2s ease;
    flex-shrink: 0;
    &.open {
      transform: rotate(180deg);
    }
  }
}
.mf-month-layer {
  position: absolute;
  top: 44px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 121px;
  padding: 15px 8px;
  gap: 11px;
  border-radius: 15px;
  background: #fafafa;
  filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.15));
  z-index: 10;
  box-sizing: border-box;
}

.mf-month-layer__group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  /* 뱃지 유무와 무관하게 라벨 위치가 일정하도록 뱃지 포함 시 너비로 고정.
     (label 40px + 뱃지 약 54px ≈ 94px) */
  min-width: 94px;
}

.mf-month-layer__item {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  text-align: left;
  color: #000;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  display: grid;
  grid-template-columns: 40px auto;
  align-items: center;

  &.active {
    color: #eb4c10;
  }
}

.mf-month-layer__item-label {
  grid-column: 1;
  white-space: nowrap;
}

.mf-month-layer__badge {
  grid-column: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 7px;
  border-radius: 10.5px;
  background: #f9f5eb;
  color: #000;
  font-family: Pretendard;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  justify-self: start;
  align-self: center;
  transform: translateY(-2px);
}

// 표지 일러스트 (月支별 통합 SVG)
.mf-envelope {
  position: absolute;
  left: 40px;
  right: 40px;
  top: 204px;
  max-width: 472px;
  height: 498px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
  }
}

.mf-cover__badge {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  align-items: center;

  &-tag {
    padding: 4px 10px;
    border-radius: 12px;
    background: #fff;
    color: #eb4c10;
    font-size: 13px;
    font-weight: 700;
  }
}

// ─── 우측 카드 목차 ─────────────────────────────────────────
.mf-toc {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  overflow-y: auto;
}

// ─── 전체 패키지 구매 완료 배너 (Figma node 2468:3920) ──────────
.mf-bundle-done {
  position: relative;
  container-type: inline-size;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: #edf8e1;
  box-sizing: border-box;
  flex-shrink: 0;

  &__check {
    width: 72px;
    height: 72px;
    flex-shrink: 0;
    display: block;
  }

  &__center {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 1 0 0;
    min-width: 0;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 0.25em;
    color: #333;
    font-family: Pretendard;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
    white-space: nowrap;
  }

  &__title-accent {
    color: #5e9f1c;
  }

  // "구매 완료" 옆 P 뱃지 + 강조선
  &__pop-svg {
    width: 56px;
    height: 36px;
    overflow: visible;
    flex-shrink: 0;
    margin-left: 4px;
    margin-top: -8px;
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;
    white-space: nowrap;
  }

  &__gift {
    position: absolute;
    right: 8px;
    top: 22px;
    width: 77px;
    height: 77px;
    object-fit: contain;
    pointer-events: none;
  }
}

// ─── 전체 패키지 배너 (Figma node 2468:3367, w-520 spec) ─────────
/* 결제 확인 팝업 내 분석권 업셀 텍스트 버튼 (confirmComponent #extra 슬롯) */
.mf-bundle-upsell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 8px auto 0;
  padding: 6px 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Gray-600, #757575);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  &__pct {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }
  &__arrow {
    color: var(--Gray-500, #949494);
    font-size: 16px;
    line-height: 1;
  }
}

.mf-bundle {
  position: relative;
  container-type: inline-size;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 20px 16px;
  border-radius: 12px;
  background: #edf8e1;
  box-sizing: border-box;
  flex-shrink: 0;

  &__main {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1 1 auto;
    min-width: 0;
  }

  // 별 + 할인 텍스트 (Figma size-87)
  &__badge {
    position: relative;
    width: 87px;
    height: 87px;
    flex-shrink: 0;
  }

  &__badge-star {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }

  &__badge-text {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.1;
    text-align: center;
    pointer-events: none;

    span {
      display: block;
    }
  }

  // 타이틀 + 가격박스
  &__center {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    min-width: 0;
    flex: 1 1 auto;
  }

  // Figma 22px bold tracking 0.66
  &__title {
    color: #333;
    font-family: Pretendard;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: 0.66px;
    margin: 0;
    text-align: center;
    white-space: nowrap;
  }

  // Figma px-12 py-8 rounded-8 — 라벨 행 / 가격 행 grid 정렬
  &__price-box {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 8px;
    row-gap: 4px;
    align-items: center;
    justify-items: center;
    width: 100%;
    padding: 8px 12px;
    border-radius: 8px;
    background: #fff;
    box-sizing: border-box;
  }

  &__price-label {
    color: #575757;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;

    &--left {
      grid-column: 1;
      grid-row: 1;
    }

    &--right {
      grid-column: 3;
      grid-row: 1;
      color: #333;
      font-size: 18px;
      font-weight: 600;
    }
  }

  &__price-base {
    grid-column: 1;
    grid-row: 2;
    color: #575757;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    text-decoration: line-through;
    white-space: nowrap;
  }

  &__price-final {
    grid-column: 3;
    grid-row: 2;
    color: #5e9f1c;
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  &__arrow {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: center;
    flex-shrink: 0;
  }

  // 버튼 + 선물 그룹
  &__right {
    position: relative;
    display: flex;
    align-items: flex-end;
    flex-shrink: 0;
  }

  // 선물 일러스트 — 버튼 위쪽에 떠 있고 버튼 영역을 살짝 덮음 (Figma)
  &__gift {
    position: absolute;
    right: -8px;
    bottom: 22px;
    width: 93px;
    height: 93px;
    object-fit: contain;
    pointer-events: none;
    z-index: 1;
  }

  &__btn {
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 38px;
    padding: 0 12px;
    border: none;
    border-radius: 6px;
    background: #5e9f1c;
    color: #fff;
    font-family: Pretendard;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    transition: filter 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__btn-label {
    font-size: 15px;
    font-weight: 600;
  }

  &__btn-price {
    font-size: 18px;
    font-weight: 700;
  }
}

.mf-toc__card {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.97);
  }

  &--disabled,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: grayscale(0.4);

    &:hover {
      filter: grayscale(0.4);
    }
  }

  // 테마별 컬러
  &--green {
    background: #f7faf3;
  }
  &--yellow {
    background: #fef7e8;
  }
  &--pink {
    background: #fff3f3;
  }
  &--blue {
    background: #eef4ff;
  }
  &--white {
    background: #fff;
    border: 1px solid #ebebeb;
  }
}

.mf-toc__card-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.mf-toc__card-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.mf-toc__card-desc {
  padding-left: 24px;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mf-toc__card-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.mf-toc__card-price,
.mf-toc__card-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 100px;
  background: #fff;
  box-shadow: 0 2px 1.5px rgba(0, 0, 0, 0.15);
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}

.mf-toc__card-price {
  color: #5e9f1c;
  font-weight: 500;
}

.mf-toc__card-badge {
  color: #538c18;
}


// 결제 확인 팝업 — 첫 줄(카드 제목) 강조
.mf-purchase-dialog :deep(.v-dialog-alert .tit-box > div:first-child) {
  color: #333;
  font-weight: 700;
  font-size: 15px;
  line-height: 24px;
  margin-bottom: 4px;
}
</style>
